import React from 'react';

/**
 * Mockup block — illustrative product preview. We render a stylised SVG
 * dashboard that picks up the active theme via CSS custom properties.
 * data: { variant?: 'dashboard' | 'thinking' | 'plain' }
 */
export default function Mockup({ data = {} }) {
  const { variant = 'dashboard' } = data;
  return (
    <section className="py-10" data-testid="site-block-mockup">
      <div className="max-w-6xl mx-auto px-6">
        <div className="rounded-3xl border orka-border-default orka-surface-raised overflow-hidden" data-testid={`site-block-mockup-${variant}`}>
          {variant === 'thinking' ? <ThinkingMockup /> : <DashboardMockup />}
        </div>
      </div>
    </section>
  );
}

function DashboardMockup() {
  return (
    <svg viewBox="0 0 1200 640" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" role="img" aria-label="Orka dashboard preview">
      <defs>
        <linearGradient id="m_bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--orka-surface-page, #fafaf9)" />
          <stop offset="100%" stopColor="var(--orka-surface-base, #f5f5f4)" />
        </linearGradient>
      </defs>
      <rect width="1200" height="640" fill="url(#m_bg)" />
      {/* sidebar */}
      <rect x="0" y="0" width="220" height="640" fill="var(--orka-surface-base, #f5f5f4)" />
      <rect x="20" y="32" width="120" height="14" rx="4" fill="var(--orka-text-primary, #0f172a)" opacity="0.9" />
      {[80, 120, 160, 200, 240, 280, 320].map((y, i) => (
        <g key={i}>
          <rect x="20" y={y} width="14" height="14" rx="3" fill="var(--orka-text-tertiary, #64748b)" opacity="0.5" />
          <rect x="44" y={y + 3} width={i === 1 ? 110 : 90} height="8" rx="3" fill="var(--orka-text-secondary, #475569)" opacity={i === 1 ? 1 : 0.4} />
        </g>
      ))}
      {/* main pane */}
      <rect x="248" y="28" width="660" height="280" rx="20" fill="var(--orka-surface-raised, #ffffff)" stroke="var(--orka-border-subtle, #e7e5e4)" />
      <rect x="270" y="56" width="180" height="14" rx="4" fill="var(--orka-text-primary, #0f172a)" />
      <rect x="270" y="80" width="280" height="10" rx="3" fill="var(--orka-text-secondary, #475569)" opacity="0.6" />
      {[120, 156, 192, 228].map((y, i) => (
        <g key={i}>
          <rect x="270" y={y} width="616" height="28" rx="8" fill="var(--orka-surface-base, #f5f5f4)" />
          <circle cx="288" cy={y + 14} r="5" fill={['#7c3aed', '#0891b2', '#16a34a', '#f59e0b'][i]} />
          <rect x="304" y={y + 10} width={[260, 360, 200, 320][i]} height="8" rx="3" fill="var(--orka-text-primary, #0f172a)" opacity="0.7" />
        </g>
      ))}
      {/* right pane (thinking) */}
      <rect x="928" y="28" width="248" height="584" rx="20" fill="var(--orka-surface-raised, #ffffff)" stroke="var(--orka-border-subtle, #e7e5e4)" />
      <rect x="948" y="56" width="120" height="12" rx="3" fill="var(--orka-text-intent, #7c3aed)" />
      {[88, 120, 152, 184, 216, 248, 280].map((y, i) => (
        <rect key={i} x="948" y={y} width={[180, 140, 200, 160, 130, 180, 110][i]} height="8" rx="3" fill="var(--orka-text-secondary, #475569)" opacity="0.5" />
      ))}
      {/* deck */}
      <rect x="248" y="328" width="660" height="284" rx="20" fill="var(--orka-surface-raised, #ffffff)" stroke="var(--orka-border-subtle, #e7e5e4)" />
      <rect x="270" y="356" width="120" height="12" rx="3" fill="var(--orka-text-primary, #0f172a)" />
      {[0, 1, 2].map(i => (
        <g key={i}>
          <rect x={270 + i * 215} y="392" width="200" height="200" rx="14" fill="var(--orka-surface-base, #f5f5f4)" stroke="var(--orka-border-subtle, #e7e5e4)" />
          <rect x={284 + i * 215} y="412" width={[120, 90, 140][i]} height="10" rx="3" fill="var(--orka-text-primary, #0f172a)" opacity="0.8" />
          <rect x={284 + i * 215} y="432" width="170" height="6" rx="2" fill="var(--orka-text-secondary, #475569)" opacity="0.4" />
          <rect x={284 + i * 215} y="446" width="150" height="6" rx="2" fill="var(--orka-text-secondary, #475569)" opacity="0.4" />
          <rect x={284 + i * 215} y="556" width="80" height="22" rx="11" fill={['#7c3aed', '#0891b2', '#16a34a'][i]} opacity="0.85" />
        </g>
      ))}
    </svg>
  );
}

function ThinkingMockup() {
  return (
    <svg viewBox="0 0 1200 480" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" role="img" aria-label="Orka thinking panel preview">
      <rect width="1200" height="480" fill="var(--orka-surface-page, #fafaf9)" />
      <rect x="40" y="40" width="1120" height="400" rx="24" fill="var(--orka-surface-raised, #ffffff)" stroke="var(--orka-border-default, #d6d3d1)" />
      <rect x="80" y="80" width="240" height="14" rx="4" fill="var(--orka-text-intent, #7c3aed)" />
      {[120, 156, 192, 228, 264, 300].map((y, i) => (
        <g key={i}>
          <circle cx="92" cy={y + 4} r="6" fill={i === 0 ? '#16a34a' : '#7c3aed'} opacity={i > 2 ? 0.3 : 1} />
          <rect x="112" y={y} width={[640, 540, 720, 480, 360, 280][i]} height="8" rx="3" fill="var(--orka-text-primary, #0f172a)" opacity={i > 2 ? 0.4 : 0.85} />
        </g>
      ))}
    </svg>
  );
}
